import { Mark, mergeAttributes } from "@tiptap/core";

export const Highlight = Mark.create({
  name: "highlight",

  addAttributes() {
    return {
      color: {
        default: "bg-yellow-300", // fallback color
        parseHTML: (element) => element.getAttribute("class"),
        renderHTML: (attributes) => ({
          class: attributes.color,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },
});
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
export const KeywordHighlighter = Extension.create({
  name: "keywordHighlighter",

  addOptions() {
    return {
      keywords: [],
      enabled: false,
      colors: [],
    };
  },

  addStorage() {
    return {
      keywords: [],
      enabled: false,
      colors: [],
    };
  },

  addCommands() {
    return {
      addKeywords:
        (keywords) =>
        ({ editor }) => {
          this.storage.keywords = keywords.map((k) => k.trim().toLowerCase());
          return true;
        },

      addColors:
        (colors) =>
        ({ editor }) => {
          this.storage.colors = colors;
          return true;
        },

      toggleHighlighting:
        (value) =>
        ({ editor }) => {
          this.storage.enabled = value;

          return true;
        },

      highlightKeywords:
        () =>
        ({ state, dispatch }) => {
          const { doc, schema } = state;
          const tr = state.tr;
          const markType = schema.marks.highlight;
          const { keywords, enabled, colors } = this.storage;

          // Remove all highlight marks first
          doc.descendants((node, pos) => {
            if (node.isText && node.marks.length) {
              node.marks.forEach((mark) => {
                if (mark.type === markType) {
                  tr.removeMark(pos, pos + node.nodeSize, markType);
                }
              });
            }
          });

          if (!enabled || !keywords.length) {
            if (tr.docChanged) dispatch(tr);
            return true;
          }

          // Highlight again if enabled
          const regexList = keywords.map((k) => ({
            keyword: k,
            regex: new RegExp(`${k.trim()}\\b`, "gi"),
          }));

          const used = new Set();
          let insideTargetZone = false;

          doc.descendants((node, pos, parent) => {
            if (node.type.name === "introzone") {
              insideTargetZone = true;
              return true;
            }

            if (!node.isText || !insideTargetZone) return;

            for (let i = 0; i < regexList.length; i++) {
              const { regex } = regexList[i];
              let match;
              while ((match = regex.exec(node.text || "")) !== null) {
                const start = pos + match.index;
                const end = start + match[0].length;

                const span = `${start}-${end}`;
                if (used.has(span)) continue;
                used.add(span);

                const color = colors[i % colors.length];
                tr.addMark(start, end, markType.create({ color }));
              }
            }
          });

          if (tr.docChanged) dispatch(tr);
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const pluginKey = new PluginKey("keywordHighlighter");

    return [
      new Plugin({
        key: pluginKey,

        props: {
          handleDOMEvents: {
            beforeinput: (view, event) => {
              const { state } = view;
              const { from, to } = state.selection;
              const highlightMarks = state.doc.rangeHasMark(
                from,
                to,
                state.schema.marks.highlight
              );

              if (highlightMarks) {
                event.preventDefault();
                return true;
              }
              return false;
            },
          },
        },

        view: (view) => {
          // Initial highlight
          setTimeout(() => {
            view.dispatch(view.state.tr); // triggers plugin update
          }, 10);

          return {
            update: (view, prevState) => {
              const docChanged = !view.state.doc.eq(prevState.doc);
              const selectionChanged = !view.state.selection.eq(
                prevState.selection
              );
              if (docChanged || selectionChanged) {
                view.dispatch(view.state.tr); // triggers highlight update via command
              }
            },
          };
        },
      }),
    ];
  },
});
